import { Children, forwardRef, isValidElement, useLayoutEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { usePortalContainer, useOverlayLayer, ModalLayerProvider } from './portal-container'
import { placed, placedChild } from '@/lib/style-guard'
import { contentClasses, type ContentProps } from '@/lib/content-props'

const Modal = DialogPrimitive.Root
const ModalTrigger = DialogPrimitive.Trigger
const ModalClose = DialogPrimitive.Close
const ModalPortal = DialogPrimitive.Portal

const ModalOverlay = forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  placedChild(props.asChild ? props.children : null, 'ModalOverlay')
  return (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-[var(--z-modal)] bg-[var(--modal-backdrop)]',
      'data-[state=open]:animate-in data-[state=open]:fade-in-0',
      'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
      placed(className, 'ModalOverlay', props)
    )}
    {...props}
  />
  )
})
ModalOverlay.displayName = 'ModalOverlay'

interface ModalContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** `wide`: a full preview (up to 1600px, 96vw), for content shown at its real width. */
  size?: 'sm' | 'md' | 'lg' | 'wide'
  /** Reserve the existing viewport cap for async lists, including loading/empty/error states. */
  height?: 'content' | 'viewport'
  /**
   * When true (default), the modal is a bottom-sheet below `md` (768px) and a
   * centered card at `md` and above. Set false for destructive confirms, where
   * a sheet dismissed by a swipe is the wrong gesture (22d rule 5) — those stay
   * centered at every width.
   *
   * 768px, not 640: it is the real phone/tablet boundary — iPad portrait is
   * 768pt and iPadOS switches to centered presentation there. At 640 a
   * landscape phone gets a centered card out of thumb reach; at 1024 a
   * ~1000px-wide sheet is mostly empty space. The bottom NAV switches at `lg`
   * instead, and that is not an inconsistency: the nav lives until the sidebar
   * appears, the sheet lives until the screen is wide enough to center a card.
   */
  mobileBottomSheet?: boolean
  /**
   * Portal mount node. Defaults to the brand-scoped app-shell (via
   * PortalContainerProvider) so white-label tenant CSS vars reach the dialog;
   * falls back to `document.body` outside that provider. Pass explicitly to
   * override.
   */
  container?: HTMLElement | null
}

const ModalContent = forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  ModalContentProps
>(({ className, children, size = 'md', height = 'content', mobileBottomSheet = true, container, ...props }, ref) => {
  placedChild(props.asChild ? children : null, 'ModalContent')
  const ctxContainer = usePortalContainer()
  const layer = useOverlayLayer('modal')
  return (
  <ModalPortal container={container ?? ctxContainer ?? undefined}>
    <ModalOverlay style={layer} />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed z-[var(--z-modal)] flex flex-col bg-[var(--surface-default)] shadow-[var(--modal-shadow)]',
        mobileBottomSheet
          ? [
              // Mobile bottom-sheet: full-width anchored bottom, slides up.
              'inset-x-0 bottom-0 w-full max-h-[85dvh] rounded-t-[var(--modal-radius)] border-t border-[var(--border-default)]',
              'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-bottom',
              'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-bottom',
              // md+ overrides: centered card, zoom animation.
              //
              // `right-auto`, NOT `inset-x-auto`. Mixing an `inset-x-*`
              // utility with `left-*` on the same element puts two rules for
              // the same property in one media query, and `inset-x` won —
              // computed `left` came out `0px` instead of `50%`, so the
              // centering translate pulled the whole dialog off the left edge.
              // Measured at 768px before the fix: x = -200, half the card
              // outside the viewport. It hit EVERY bottom-sheet modal between
              // 640px and 1023px on EVERY bottom-sheet modal, which is the
              // default. Reported by David as "the modal opens outside the
              // viewport" when tapping Mer in the bottom nav.
              'md:left-1/2 md:top-1/2 md:bottom-auto md:right-auto md:-translate-x-1/2 md:-translate-y-1/2',
              'md:max-h-[90dvh] md:rounded-[var(--modal-radius)] md:border md:border-[var(--border-default)]',
              'md:data-[state=open]:zoom-in-95 md:data-[state=open]:slide-in-from-left-1/2 md:data-[state=open]:slide-in-from-top-[48%]',
              'md:data-[state=open]:slide-in-from-bottom-0',
              'md:data-[state=closed]:zoom-out-95 md:data-[state=closed]:slide-out-to-bottom-0',
            ]
          : [
              'left-1/2 top-1/2 w-full -translate-x-1/2 -translate-y-1/2 max-h-[90dvh]',
              'rounded-[var(--modal-radius)] border border-[var(--border-default)]',
              'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
              'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            ],
        'duration-200',
        size === 'sm' && 'md:max-w-[var(--modal-max-width-sm)]',
        size === 'md' && 'md:max-w-[var(--modal-max-width-md)]',
        size === 'lg' && 'md:max-w-[var(--modal-max-width-lg)]',
        size === 'wide' && 'md:max-w-[min(96vw,1600px)]',
        height === 'viewport' && (mobileBottomSheet ? 'h-[85dvh] md:h-[90dvh]' : 'h-[90dvh]'),
        placed(className, 'ModalContent', props)
      )}
      {...props}
      style={{ ...props.style, ...layer }}
    >
      <ModalLayerProvider>{children}</ModalLayerProvider>
    </DialogPrimitive.Content>
  </ModalPortal>
  )
})
ModalContent.displayName = 'ModalContent'

function ModalHeader({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const items = Children.toArray(children)
  const isHeading = (child: React.ReactNode) => isValidElement(child) && (child.type === ModalTitle || child.type === ModalDescription)
  // Direct title/description pairs are one text column. Existing composed
  // headers and title-only action rows retain their current layout.
  const groupHeading = items.some(child => isValidElement(child) && child.type === ModalDescription) && items.some(child => isValidElement(child) && child.type === ModalTitle)
  const contents = groupHeading ? [
    <div key="heading" className="min-w-0 flex-1 space-y-1">{items.filter(isHeading)}</div>,
    ...items.filter(child => !isHeading(child)),
  ] : children
  return (
    <div
      className={cn(
        'flex items-center justify-between p-[var(--modal-padding)] pb-0',
        groupHeading && 'gap-3',
        placed(className, 'ModalHeader', props)
      )}
      {...props}
    >{contents}</div>
  )
}

/* ModalBody looks (30.10.2026): `sheet` is a bottom sheet's list body (tight top, safe-area bottom),
   `sunken` a recessed well for quoted content, `spaced` a body that opens with breathing room. */
function ModalBody({ className, align, clamp, wrap, numeric, focus, tabIndex, look, ...props }: (React.HTMLAttributes<HTMLDivElement> & { look?: 'sheet' | 'sunken' | 'spaced' }) & ContentProps) {
  const bodyRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const body = bodyRef.current
    if (!body || tabIndex !== undefined) return
    // Text-only scrollports need their own keyboard stop. Forms already have
    // controls, which must retain Radix's initial focus and normal tab order.
    const update = () => {
      const controls = body.querySelectorAll<HTMLElement>('a[href], button, input, select, textarea, [tabindex], [contenteditable="true"]')
      const hasControl = Array.from(controls).some(control => {
        if (control.tabIndex < 0 || control.matches(':disabled, input[type="hidden"]') || control.closest('[hidden], [inert]')) return false
        for (let node: HTMLElement | null = control; node && node !== body; node = node.parentElement) {
          const style = getComputedStyle(node)
          if (style.display === 'none' || style.visibility === 'hidden') return false
        }
        return true
      })
      const next = hasControl ? '-1' : '0'
      if (body.getAttribute('tabindex') !== next) body.setAttribute('tabindex', next)
    }
    update()
    // Async history or form controls can replace a loading state while open.
    const observer = new MutationObserver(update)
    observer.observe(body, { childList: true, subtree: true, attributes: true,
      attributeFilter: ['disabled', 'hidden', 'inert', 'tabindex', 'class', 'style', 'href', 'contenteditable', 'type'] })
    return () => observer.disconnect()
  }, [tabIndex])
  return (
    <div ref={bodyRef} tabIndex={tabIndex} className={cn('flex-1 overflow-y-auto p-[var(--modal-padding)] focus-ring', look === 'sheet' && 'pt-2 pb-[max(var(--modal-padding),env(safe-area-inset-bottom))]', look === 'sunken' && 'bg-[var(--surface-sunken)]', look === 'spaced' && 'pt-6', contentClasses({ align, clamp, wrap, numeric, focus }), placed(className, 'ModalBody', props))} {...props} />
  )
}

/**
 * The modal's action row. **It is already pinned to the modal's bottom — do
 * not add `sticky`, `fixed` or `absolute` to it.**
 *
 * The guarantee comes from three pieces together: `ModalContent` is
 * `flex flex-col` under a `max-h-[85dvh]`/`md:max-h-[90dvh]` cap, `ModalBody`
 * is `flex-1 overflow-y-auto`, and this row is the last child. However tall
 * the body grows, it scrolls inside the body and this row stays visible. That
 * holds at every viewport and needs no help.
 *
 * `AgendaTab` added `sticky bottom-0 z-10` here anyway, reasoning that a tall
 * modal would scroll its actions away. Measured 11.08.2026 at 1440×900: it put
 * the row at `top: 426`, on top of the form fields, with the rest of the form
 * rendering below it — and it did not move when the body scrolled, because
 * every ancestor (dialog, app shell, body, html) is `overflow: hidden` with
 * `scrollHeight === clientHeight`, so `bottom: 0` had no scrollport to resolve
 * against. `position: static` on the same node lands it at `785`, correctly.
 * David reported it as the save button landing on top of the fields.
 *
 * `className` stays open, because `justify-between` (RoomEditorModal) is a
 * real need. Positioning is not one of them, and this component cannot defend
 * itself: `cn()` is `clsx` with no tailwind-merge, so a caller's classes are
 * simply appended. The rule lives here because this is where the next reader
 * already is.
 */
function ModalFooter({ className, divided = false, ...props }: React.HTMLAttributes<HTMLDivElement> & { divided?: boolean }) {
  return (
    <div
      className={cn(
        // Full-width feedback precedes the actions instead of becoming a narrow column.
        'flex flex-wrap items-center justify-end gap-3 p-[var(--modal-padding)] pt-0',
        // Bottom padding always matches the modal's own padding, and grows to
        // clear the iOS home-indicator when the modal is bottom-anchored on
        // mobile. (A former `sm:pb-0` zeroed this on every screen >=640px,
        // leaving footer buttons flush against the modal edge.)
        'pb-[max(var(--modal-padding),env(safe-area-inset-bottom))]',
        divided && 'border-t border-[var(--border-default)] bg-[var(--surface-elevated)]',
        placed(className, 'ModalFooter', props)
      )}
      {...props}
    />
  )
}

function ModalTitle({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>) {
  placedChild(props.asChild ? props.children : null, 'ModalTitle')
  return (
    <DialogPrimitive.Title
      className={cn('text-h3 text-[var(--text-primary)]', placed(className, 'ModalTitle', props))}
      {...props}
    />
  )
}

/**
 * The dialog's own one-line answer to «what is this for».
 *
 * ## Why it did not exist until 16.08.2026
 *
 * It simply had not been written. Measured on `main`: 28 `<ModalContent>` call
 * sites, zero descriptions, and Radix saying so 11 times in one test run —
 * «Missing `Description` or `aria-describedby={undefined}` for {DialogContent}».
 * A screen-reader user opening «Ny gruppe» heard the title and then the raw
 * form; a sighted reader has the whole card to infer from, which is exactly the
 * asymmetry a description closes.
 *
 * Radix accepts two answers and refuses silence: a `Description`, or an
 * explicit `aria-describedby={undefined}` meaning «none, deliberately».
 *
 * ⚠️ The opt-out is NOT applied inside `ModalContent`. Doing that would clear
 * all 11 warnings in one line and destroy the only detector the app has for the
 * 25 dialogs still missing one — and the warning is now a build failure
 * (`vitest.config.ts` · `onConsoleLog`), so a dialog gets its description as
 * soon as it gets a test. Silence the messenger and that ratchet stops turning.
 */
function ModalDescription({
  className, align, clamp, wrap, numeric, focus,
  inset = false,
  size = 'default',
  ...props
}: (React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & {
  /** Sits in the body's column rather than under the header: the modal's own side padding. */
  inset?: boolean
  size?: 'default' | 'sm' | 'body'
}) & ContentProps) {
  placedChild(props.asChild ? props.children : null, 'ModalDescription')
  return (
    <DialogPrimitive.Description
      className={cn(size === 'sm' ? 'text-2xs' : size === 'body' ? 'text-body' : 'text-small', 'text-[var(--text-secondary)]', inset && 'px-[var(--modal-padding)]', contentClasses({ align, clamp, wrap, numeric, focus }), placed(className, 'ModalDescription', props))}
      {...props}
    />
  )
}

function ModalCloseButton() {
  const { t } = useTranslation()
  const label = t('common.close')
  return (
    <DialogPrimitive.Close
      aria-label={label}
      title={label}
      className="focus-ring inline-flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-[var(--radius-md)] text-[var(--text-muted)] transition-colors hover:bg-[var(--surface-muted)] hover:text-[var(--text-primary)]"
    >
      <X className="size-5" aria-hidden="true" />
    </DialogPrimitive.Close>
  )
}

export {
  Modal,
  ModalTrigger,
  ModalClose,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalTitle,
  ModalDescription,
  ModalCloseButton,
}
